// VectorDisplay.tsx
// Visual indicator showing rotation vector from initial to current position

import React from 'react';

interface VectorDisplayProps {
  vector: { x: number; y: number; z: number };
}

export const VectorDisplay: React.FC<VectorDisplayProps> = ({ vector }) => {
  // Calculate arrow rotation angle (in 2D projection)
  const angle = Math.atan2(vector.y, vector.x) * (180 / Math.PI);
  
  // Calculate magnitude
  const magnitude = Math.sqrt(vector.x ** 2 + vector.y ** 2 + vector.z ** 2);
  
  return (
    <div className="absolute bottom-4 right-4 bg-gray-800 bg-opacity-90 p-4 rounded-lg min-w-[200px]">
      <h4 className="text-sm font-semibold mb-3 text-gray-300">Rotation Vector</h4>
      
      {/* Arrow indicator */}
      <div className="flex items-center justify-center mb-3 h-16 relative">
        <svg width="80" height="80" className="overflow-visible">
          {/* Circle background */}
          <circle cx="40" cy="40" r="30" fill="none" stroke="#374151" strokeWidth="1" />
          
          {/* Center dot */}
          <circle cx="40" cy="40" r="2" fill="#ec4899" />
          
          {/* Arrow */}
          <g transform={`rotate(${-angle} 40 40)`}>
            <line 
              x1="40" 
              y1="40" 
              x2="40" 
              y2={40 - Math.min(magnitude * 25, 28)} 
              stroke="#ec4899" 
              strokeWidth="2"
            />
            <polygon
              points={`40,${40 - Math.min(magnitude * 25, 28) - 5} 37,${40 - Math.min(magnitude * 25, 28)} 43,${40 - Math.min(magnitude * 25, 28)}`}
              fill="#ec4899"
            />
          </g>
        </svg>
      </div>
      
      {/* Vector components */}
      <div className="space-y-1 font-mono text-xs">
        <div className="flex justify-between">
          <span className="text-gray-400">x:</span>
          <span className="text-pink-400">{vector.x.toFixed(3)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">y:</span>
          <span className="text-pink-400">{vector.y.toFixed(3)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-400">z:</span>
          <span className="text-pink-400">{vector.z.toFixed(3)}</span>
        </div>
        <div className="flex justify-between pt-2 border-t border-gray-700">
          <span className="text-gray-400">|v|:</span>
          <span className="text-blue-400">{magnitude.toFixed(3)}</span>
        </div>
      </div>
    </div>
  );
};